<template>
  <div class="chat-window-container">
    <div v-if="messages.length === 0 && !isLoadingMessages" class="empty-chat-state">
      <div class="welcome-message">
        <h2 class="tech-glitch-text">{{ emptyStateTitle }}</h2>
        <p>{{ emptyStateSubtitle }}</p>
      </div>
    </div>

    <div v-else class="messages-list">
      <div v-if="isLoadingMessages" class="loading-animation initial-load">
        <div class="tech-dot"></div>
        <div class="tech-dot"></div>
        <div class="tech-dot"></div>
      </div>

      <ChatMessage
          v-for="message in messages"
          :key="message.id"
          :message="message"
          :should-type="message.id === typingMessageId"
      />

      <div v-if="isLoadingResponse" class="loading-animation assistant-response">
        <div class="tech-dot"></div>
        <div class="tech-dot"></div>
        <div class="tech-dot"></div>
      </div>
    </div>

    <div class="input-area">
      <div class="input-glow-line"></div>

      <el-input
          type="textarea"
          :placeholder="enterMessagePlaceholder"
          v-model="inputText"
          @keydown.enter.native="handleEnterKey"
          resize="none"
          :autosize="{ minRows: 2, maxRows: 6 }"
          class="message-textarea tech-input"
          :disabled="isLoadingResponse || isLoadingMessages"
      ></el-input>

      <el-button
          type="primary"
          icon="el-icon-s-promotion"
          @click="sendMessage"
          :disabled="!inputText.trim() || isLoadingResponse || isLoadingMessages"
          class="send-button tech-send-btn"
      ></el-button>
    </div>
  </div>
</template>

<script>
import ChatMessage from './ChatMessage.vue';

export default {
  name: 'ChatWindow',
  components: {
    ChatMessage
  },
  props: {
    messages: { type: Array, default: () => [] },
    isChatSelected: { type: Boolean, default: false },
    isLoadingResponse: { type: Boolean, default: false },
    isLoadingMessages: { type: Boolean, default: false },
    emptyStateTitle: { type: String, default: 'Hello!' },
    emptyStateSubtitle: { type: String, default: 'How can I help you today?' },
    enterMessagePlaceholder: { type: String, default: 'Enter your message...' }
  },
  data() {
    return {
      inputText: '',
      typingMessageId: null
    };
  },
  watch: {
    messages: {
      deep: true,
      handler(newVal) {
        if (this.isLoadingResponse && newVal.length > 0) {
          const lastMsg = newVal[newVal.length - 1];
          if (lastMsg.sender === 'assistant') {
            this.typingMessageId = lastMsg.id;
          }
        }
        this.$nextTick(() => { this.scrollToBottom(); });
      }
    },
    isChatSelected(newValue) {
      if (newValue) {
        this.inputText = '';
        this.typingMessageId = null;
      }
    }
  },
  methods: {
    handleEnterKey(event) {
      // 1. 如果正在输入法编辑状态（如打拼音中），直接返回，不要发送
      if (event.isComposing) return;

      // 2. 如果正在加载，阻止输入
      if (this.isLoadingResponse || this.isLoadingMessages) {
        event.preventDefault();
        return;
      }

      // 3. 组合键支持：Shift/Ctrl/Alt/Meta + Enter = 换行
      if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) {
        // 不阻止默认行为，让 Textarea 正常换行
        return;
      }

      // 4. 纯回车：阻止换行，执行发送
      event.preventDefault();
      if (this.inputText.trim()) {
        this.sendMessage();
      }
    },
    sendMessage() {
      if (this.inputText.trim() && !this.isLoadingResponse && !this.isLoadingMessages) {
        this.$emit('send-message', this.inputText.trim());
        this.inputText = '';
      }
    },
    scrollToBottom() {
      const messagesList = this.$el.querySelector('.messages-list');
      if (messagesList) {
        messagesList.scrollTo({ top: messagesList.scrollHeight, behavior: 'smooth' });
      }
    }
  }
};
</script>

<style scoped>
/* 保持您满意的 Flex 布局样式不变 */
.chat-window-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  position: relative;
  background: transparent;
}

.empty-chat-state {
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: var(--tech-text-sub);
  padding: 20px;
}

.welcome-message h2 {
  margin-bottom: 15px;
  color: var(--tech-primary);
  font-size: 28px;
  text-shadow: 0 0 10px rgba(0, 153, 255, 0.4);
  letter-spacing: 1px;
}

.messages-list {
  flex-grow: 1;
  overflow-y: auto;
  padding: 20px 30px;
  padding-bottom: 20px;
  position: relative;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 153, 255, 0.2) transparent;
}

.input-area {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  padding: 15px 30px 20px 30px;
  background: rgba(10, 20, 35, 0.9);
  backdrop-filter: blur(10px);
  z-index: 10;
  border-top: 1px solid rgba(0, 153, 255, 0.1);
  box-shadow: 0 -5px 20px rgba(0, 0, 0, 0.5);
}

.input-glow-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--tech-primary), transparent);
  opacity: 0.5;
}

.tech-input >>> .el-textarea__inner {
  background-color: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(0, 153, 255, 0.1) !important;
  color: #fff !important;
  border-radius: 4px;
  padding: 10px 15px;
  font-family: 'Segoe UI', sans-serif;
  transition: all 0.3s ease;
  caret-color: var(--tech-primary);
}

.tech-input >>> .el-textarea__inner:focus {
  border-color: var(--tech-primary) !important;
  box-shadow: 0 0 10px rgba(0, 153, 255, 0.1);
  background-color: rgba(255, 255, 255, 0.08) !important;
}

.message-textarea {
  flex-grow: 1;
  margin-right: 15px;
}

.tech-send-btn {
  background: transparent !important;
  border: 1px solid var(--tech-primary) !important;
  color: var(--tech-primary) !important;
  padding: 10px 20px;
  height: 40px;
  border-radius: 4px;
  font-size: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 0 5px rgba(0, 153, 255, 0.2);
  margin-bottom: 2px;
}

.tech-send-btn:hover:not(.is-disabled) {
  background: var(--tech-primary) !important;
  color: #000 !important;
  box-shadow: 0 0 15px var(--tech-primary);
}

.tech-send-btn.is-disabled {
  border-color: #555 !important;
  color: #555 !important;
  box-shadow: none;
  background: transparent !important;
}

.loading-animation {
  display: flex;
  align-items: center;
  padding: 10px 0;
  margin-bottom: 15px;
}

.loading-animation.initial-load {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(5, 11, 20, 0.8);
  z-index: 5;
  justify-content: center;
}

.tech-dot {
  width: 6px;
  height: 6px;
  background-color: var(--tech-primary);
  border-radius: 50%;
  margin-right: 6px;
  animation: techBounce 1.4s infinite ease-in-out both;
  box-shadow: 0 0 5px var(--tech-primary);
}

.tech-dot:nth-child(1) { animation-delay: -0.32s; }
.tech-dot:nth-child(2) { animation-delay: -0.16s; }

@keyframes techBounce {
  0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

@media (max-width: 768px) {
  .messages-list { padding: 15px 15px; }
  .input-area { padding: 10px 15px 15px 15px; }
}
</style>